import type { PersonneFamille, DonneesArbre, Personne } from './types.js';

/**
 * Adaptateurs entre les réponses de l'API (contrat) et les données de l'écran. Aucune valeur n'est inventée : ce que
 * l'API ne porte pas reste vide (« à trouver » dans les tableaux), jamais remplacé par un exemple.
 */

/** Dévolution vide : rien n'est calculé, donc aucune quote-part. */
export function devolutionVide(successionId = ''): DonneesArbre['devolution'] {
  return {
    successionId,
    shares: [],
    reserves: [],
    explanation: [],
    status: 'a_valider_notaire',
    engineVersion: 'non-calculee',
    computedAt: '1970-01-01T00:00:00Z',
    spouseOption: null,
  };
}

/** « Odile Calixte » devient prénoms « Odile » et nom « Calixte » ; un seul mot est pris pour un prénom. */
export function separerNom(affiche: string): { firstNames: string; lastName: string } {
  const mots = affiche.trim().split(/\s+/);
  if (mots.length < 2) return { firstNames: mots.join(' '), lastName: '' };
  return { firstNames: mots.slice(0, -1).join(' '), lastName: mots[mots.length - 1] ?? '' };
}

const DATE_INCONNUE = { value: null, precision: 'inconnue' } as const;

/** Une personne de l'écran avec seulement ce que l'API a donné ; tout le reste est vide. */
export function personneVide(base: {
  id: string;
  displayName: string;
  vitalStatus: Personne['vitalStatus'];
  isHypothesis: boolean;
  relationLabel: string | null;
  dossierId?: string;
  role?: Personne['role'];
  reachability?: Personne['reachability'];
}): Personne {
  return {
    id: base.id,
    ...separerNom(base.displayName),
    birthName: null,
    birthDate: DATE_INCONNUE,
    birthPlace: null,
    deathDate: null,
    deathPlace: null,
    vitalStatus: base.vitalStatus,
    isHypothesis: base.isHypothesis,
    relationLabel: base.relationLabel,
    dossierId: base.dossierId ?? '',
    sex: null,
    role: base.role ?? 'autre',
    reachability: base.reachability ?? 'joignable',
    contacts: [],
    preferences: { preferredChannel: null, contactOnlyByCabinet: false, delegatedContactUserId: null, language: 'fr' },
    sensitiveLabels: [],
    rowVersion: 0,
  };
}

/** portail.branche : la branche de l'héritier, sans lien ni part (le contrat n'en porte pas). */
export function adapterBranche(branche: readonly PersonneFamille[]): DonneesArbre {
  return {
    personnes: branche.map((m) => ({
      ...personneVide(m),
      contacts: m.sharedContacts.map((c, i) => ({
        id: `${m.id}-contact-${i}`,
        type: c.type,
        value: c.value,
        visibility: 'famille' as const,
        maskedForFamily: false,
        isPreferred: false,
        timezone: null,
      })),
    })),
    relations: [],
    successions: [],
    devolution: devolutionVide(),
  };
}

type ArbreApi = {
  dossierId: string;
  nodes: readonly {
    id: string;
    displayName: string;
    vitalStatus: Personne['vitalStatus'];
    isHypothesis: boolean;
    role: Personne['role'];
    reachability: Personne['reachability'] | null;
    /** RFC-GENEA28-001 : coordonnées de la personne (équipe). */
    contacts?: readonly {
      type: Personne['contacts'][number]['type'];
      value: string;
      visibility: Personne['contacts'][number]['visibility'];
    }[];
  }[];
  relationships: DonneesArbre['relations'];
  successions: DonneesArbre['successions'];
  devolutions: readonly DonneesArbre['devolution'][];
};

/**
 * genealogie.arbre : l'arbre du dossier. Les devolutions (une par succession) sont réunies ; sans aucune, la dévolution
 * est marquée non calculée et ne porte aucune part. Un statut « à valider notaire » l'emporte dès qu'une dévolution l'est.
 */
export function adapterArbreEquipe(arbre: ArbreApi): DonneesArbre {
  const premiere = arbre.devolutions[0];
  const devolution: DonneesArbre['devolution'] = premiere
    ? {
        ...premiere,
        shares: arbre.devolutions.flatMap((x) => x.shares),
        reserves: arbre.devolutions.flatMap((x) => x.reserves),
        explanation: arbre.devolutions.flatMap((x) => x.explanation),
        status: arbre.devolutions.every((x) => x.status === 'valide_notaire') ? 'valide_notaire' : 'a_valider_notaire',
      }
    : devolutionVide();
  return {
    personnes: arbre.nodes.map((n) => ({
      ...personneVide({ ...n, relationLabel: null, dossierId: arbre.dossierId, reachability: n.reachability ?? 'joignable' }),
      // ARB-GENEA-28 : les coordonnées lues dans l'arbre (avant : liste vide, « à trouver » partout et aucun badge).
      contacts: (n.contacts ?? []).map((c, i) => ({
        id: `${n.id}-coordonnee-${i}`,
        type: c.type,
        value: c.value,
        visibility: c.visibility,
        maskedForFamily: c.visibility !== 'famille',
        isPreferred: i === 0,
        timezone: null,
      })),
    })),
    relations: arbre.relationships,
    successions: arbre.successions,
    devolution,
    devolutionCalculee: premiere !== undefined,
  };
}
